import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { UploadIcon, DownloadIcon, FileTextIcon, CheckIcon, XIcon, InfoIcon, AlertCircleIcon, ClockIcon, PlusIcon, FilterIcon, RefreshCwIcon, FileIcon, EyeIcon, ArrowRightIcon } from 'lucide-react';
import * as XLSX from 'xlsx';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import Modal from '../../components/ui/Modal';
import StatusBadge from '../../components/ui/StatusBadge';
import { useAuth } from '../../context/AuthContext';
// Mock data for parent merchants dropdown
const parentMerchants = [{
  id: 'M001',
  name: 'ABC Corporation'
}, {
  id: 'M002',
  name: 'XYZ Enterprises'
}, {
  id: 'M003',
  name: 'Local Grocers'
}, {
  id: 'M004',
  name: 'Tech Solutions Inc'
}, {
  id: 'M005',
  name: 'Metro Retailers'
}];
// Mock data for MCC codes
const mccCodes = [{
  code: '5411',
  description: 'Grocery Stores, Supermarkets'
}, {
  code: '5812',
  description: 'Eating Places, Restaurants'
}, {
  code: '5912',
  description: 'Drug Stores, Pharmacies'
}, {
  code: '5942',
  description: 'Book Stores'
}, {
  code: '7011',
  description: 'Hotels, Motels, Resorts'
}, {
  code: '7299',
  description: 'Other Services'
}];
// Mock data for business types
const businessTypes = ['Individual', 'Sole proprietor', 'Partnership', 'Corporate', 'Public limited'];
// Mock data for batch requests
const mockBatchRequests = [{
  id: 'BR001',
  requestedBy: 'admin@example.com',
  requestedAt: '2023-10-28T09:30:00Z',
  merchantCount: 15,
  validCount: 12,
  errorCount: 3,
  status: 'pending',
  description: 'October Batch 1 - New Merchants'
}, {
  id: 'BR002',
  requestedBy: 'admin@example.com',
  requestedAt: '2023-10-25T14:45:00Z',
  merchantCount: 8,
  validCount: 8,
  errorCount: 0,
  status: 'approved',
  description: 'October Batch 2 - Restaurant Chain',
  approvedBy: 'checker@example.com',
  approvedAt: '2023-10-26T10:15:00Z'
}, {
  id: 'BR003',
  requestedBy: 'admin@example.com',
  requestedAt: '2023-10-20T11:20:00Z',
  merchantCount: 12,
  validCount: 9,
  errorCount: 3,
  status: 'rejected',
  description: 'October Batch 3 - Retail Stores',
  rejectedBy: 'checker@example.com',
  rejectedAt: '2023-10-21T09:30:00Z',
  rejectionReason: 'Missing documentation for 5 merchants'
}];
interface ErrorDetail {
  field: string;
  message: string;
}
interface Merchant {
  id: number;
  legalName: string;
  dba: string;
  cnicNtn: string;
  address: string;
  mobile: string;
  email: string;
  city: string;
  country: string;
  accountIban: string;
  bankName: string;
  bankCode: string;
  settlementMode: string;
  mcc: string;
  businessType: string;
  monthlyTxnCount: string;
  monthlyTxnValue: string;
  status: 'pending' | 'approved' | 'rejected' | 'error';
  mid: string;
  errors: ErrorDetail[];
}
interface BatchRequest {
  id: string;
  requestedBy: string;
  requestedAt: string;
  merchantCount: number;
  validCount: number;
  errorCount: number;
  status: 'pending' | 'approved' | 'rejected';
  description: string;
  approvedBy?: string;
  approvedAt?: string;
  rejectedBy?: string;
  rejectedAt?: string;
  rejectionReason?: string;
}
// Required fields for merchant validation
const requiredFields = ['legalName', 'dba', 'cnicNtn', 'address', 'mobile', 'email', 'city', 'country', 'accountIban', 'bankName', 'bankCode', 'mcc', 'businessType', 'monthlyTxnCount', 'monthlyTxnValue'];
// Field mappings from Excel to our data model
const fieldMappings: Record<string, string> = {
  LegalName: 'legalName',
  DBA: 'dba',
  'CNIC/NTN': 'cnicNtn',
  Address: 'address',
  Mobile: 'mobile',
  Email: 'email',
  City: 'city',
  Country: 'country',
  'Account/IBAN': 'accountIban',
  'Bank Name': 'bankName',
  'Bank Code': 'bankCode',
  'Settlement Mode': 'settlementMode',
  MCC: 'mcc',
  'Business Type': 'businessType',
  MonthlyTxnCount: 'monthlyTxnCount',
  MonthlyTxnValue: 'monthlyTxnValue'
};
const BulkMerchant: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    user
  } = useAuth() || {
    user: {
      role: 'maker'
    }
  }; // Default to maker if context is not available
  const [view, setView] = useState<'upload' | 'history' | 'checker'>('upload');
  const [selectedParentMerchant, setSelectedParentMerchant] = useState<string>('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedMerchants, setUploadedMerchants] = useState<Merchant[]>([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showErrorDetailModal, setShowErrorDetailModal] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null);
  const [batchDescription, setBatchDescription] = useState('');
  const [batchRequests, setBatchRequests] = useState<BatchRequest[]>(mockBatchRequests);
  const [selectedBatchRequest, setSelectedBatchRequest] = useState<BatchRequest | null>(null);
  const [showBatchDetailModal, setShowBatchDetailModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [fileReadError, setFileReadError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  // Add state to store submission stats for the success modal
  const [submissionStats, setSubmissionStats] = useState({
    totalCount: 0,
    validCount: 0,
    errorCount: 0
  });
  // Function to validate a merchant
  const validateMerchant = (merchant: Partial<Merchant>): ErrorDetail[] => {
    const errors: ErrorDetail[] = [];
    // Check required fields
    for (const field of requiredFields) {
      if (!merchant[field as keyof Merchant] || String(merchant[field as keyof Merchant]).trim() === '') {
        errors.push({
          field,
          message: `${field.charAt(0).toUpperCase() + field.slice(1)} is required`
        });
      }
    }
    // Validate email format
    if (merchant.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(merchant.email)) {
      errors.push({
        field: 'email',
        message: 'Invalid email format'
      });
    }
    // Validate IBAN format for Pakistan
    if (merchant.accountIban && (!merchant.accountIban.startsWith('PK') || merchant.accountIban.length !== 24)) {
      errors.push({
        field: 'accountIban',
        message: 'Invalid IBAN format - must start with PK followed by 22 characters'
      });
    }
    // Validate mobile number format for Pakistan
    if (merchant.mobile && !/^\+92\s\d{3}\s\d{7}$/.test(merchant.mobile)) {
      errors.push({
        field: 'mobile',
        message: 'Invalid mobile format - must be in format +92 3XX XXXXXXX'
      });
    }
    // Validate MCC code - must be a 4-digit numeric value
    if (merchant.mcc) {
      const mccString = String(merchant.mcc).trim();
      if (!/^\d{4}$/.test(mccString)) {
        errors.push({
          field: 'mcc',
          message: 'Invalid MCC code - must be a 4-digit numeric value'
        });
      }
    }
    // Check if business type is valid
    if (merchant.businessType && !businessTypes.includes(merchant.businessType)) {
      errors.push({
        field: 'businessType',
        message: 'Invalid business type'
      });
    }
    return errors;
  };
  // Function to read Excel file
  const readExcelFile = (file: File): Promise<Merchant[]> => {
    return new Promise((resolve, reject) => {
      setIsProcessing(true);
      setFileReadError(null);
      const reader = new FileReader();
      reader.onload = e => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, {
            type: 'binary'
          });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          // Convert sheet to JSON
          const jsonData = XLSX.utils.sheet_to_json<Record<string, any>>(worksheet);
          if (jsonData.length === 0) {
            setFileReadError('No data found in the uploaded file');
            setIsProcessing(false);
            reject(new Error('No data found in the uploaded file'));
            return;
          }
          // Map Excel data to our merchant model
          const merchants: Merchant[] = jsonData.map((row, index) => {
            const merchant: Partial<Merchant> = {
              id: index + 1,
              status: 'pending',
              mid: '',
              errors: []
            };
            // Map fields from Excel to our model
            Object.keys(row).forEach(excelField => {
              const ourField = fieldMappings[excelField];
              if (ourField) {
                merchant[ourField as keyof Merchant] = row[excelField];
              }
            });
            // Validate the merchant
            const validationErrors = validateMerchant(merchant);
            // Set status based on validation
            if (validationErrors.length > 0) {
              merchant.status = 'error';
              merchant.errors = validationErrors;
            }
            return merchant as Merchant;
          });
          setIsProcessing(false);
          resolve(merchants);
        } catch (error) {
          console.error('Error parsing Excel file:', error);
          setFileReadError('Error parsing the uploaded file. Please ensure it is a valid Excel file.');
          setIsProcessing(false);
          reject(error);
        }
      };
      reader.onerror = error => {
        console.error('Error reading file:', error);
        setFileReadError('Error reading the uploaded file');
        setIsProcessing(false);
        reject(error);
      };
      // Read the file as binary
      reader.readAsBinaryString(file);
    });
  };
  // Handle file selection
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      setUploadedFile(file);
      try {
        const merchants = await readExcelFile(file);
        setUploadedMerchants(merchants);
        setShowPreviewModal(true);
      } catch (error) {
        console.error('Failed to process file:', error);
        // Error is already set in the readExcelFile function
      }
    }
  };
  // Trigger file input click
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  // Get validation summary
  const getValidationSummary = () => {
    const validCount = uploadedMerchants.filter(m => m.errors.length === 0).length;
    const errorCount = uploadedMerchants.filter(m => m.errors.length > 0).length;
    return {
      validCount,
      errorCount,
      totalCount: uploadedMerchants.length
    };
  };
  // Handle batch submission
  const handleSubmitBatch = () => {
    setIsSubmitting(true);
    // Filter out merchants with errors
    const validMerchants = uploadedMerchants.filter(m => m.errors.length === 0);
    const validationSummary = getValidationSummary();
    // Store the validation summary for the success modal
    setSubmissionStats({
      totalCount: validationSummary.totalCount,
      validCount: validationSummary.validCount,
      errorCount: validationSummary.errorCount
    });
    // Simulate API call
    setTimeout(() => {
      const newBatchId = `BR${String(batchRequests.length + 1).padStart(3, '0')}`;
      const newBatch: BatchRequest = {
        id: newBatchId,
        requestedBy: 'admin@example.com',
        requestedAt: new Date().toISOString(),
        merchantCount: validationSummary.totalCount,
        validCount: validationSummary.validCount,
        errorCount: validationSummary.errorCount,
        status: 'pending',
        description: batchDescription || `Batch Upload - ${new Date().toLocaleDateString()}`
      };
      setBatchRequests([newBatch, ...batchRequests]);
      setIsSubmitting(false);
      setShowSubmitModal(false);
      setShowSuccessModal(true);
      // Reset upload state
      setUploadedFile(null);
      setUploadedMerchants([]);
      setBatchDescription('');
    }, 1500);
  };
  // Handle batch approval
  const handleApproveBatch = () => {
    if (!selectedBatchRequest) return;
    const updatedBatches = batchRequests.map(batch => batch.id === selectedBatchRequest.id ? {
      ...batch,
      status: 'approved' as const,
      approvedBy: 'checker@example.com',
      approvedAt: new Date().toISOString()
    } : batch);
    setBatchRequests(updatedBatches);
    setShowApproveModal(false);
    setShowBatchDetailModal(false);
    setSelectedBatchRequest(null);
  };
  // Handle batch rejection
  const handleRejectBatch = () => {
    if (!selectedBatchRequest) return;
    const updatedBatches = batchRequests.map(batch => batch.id === selectedBatchRequest.id ? {
      ...batch,
      status: 'rejected' as const,
      rejectedBy: 'checker@example.com',
      rejectedAt: new Date().toISOString(),
      rejectionReason
    } : batch);
    setBatchRequests(updatedBatches);
    setShowRejectModal(false);
    setShowBatchDetailModal(false);
    setSelectedBatchRequest(null);
    setRejectionReason('');
  };
  // Handle export all merchants with status
  const handleExportAll = () => {
    if (uploadedMerchants.length === 0) return;
    // Create a workbook with the merchant data
    const wb = XLSX.utils.book_new();
    // Prepare data for export with error details
    const exportData = uploadedMerchants.map(merchant => {
      const exportRow: Record<string, any> = {
        'Legal Name': merchant.legalName,
        DBA: merchant.dba,
        'CNIC/NTN': merchant.cnicNtn,
        Address: merchant.address,
        Mobile: merchant.mobile,
        Email: merchant.email,
        City: merchant.city,
        Country: merchant.country,
        'Account/IBAN': merchant.accountIban,
        'Bank Name': merchant.bankName,
        'Bank Code': merchant.bankCode,
        'Settlement Mode': merchant.settlementMode,
        MCC: merchant.mcc,
        'Business Type': merchant.businessType,
        'Monthly Txn Count': merchant.monthlyTxnCount,
        'Monthly Txn Value': merchant.monthlyTxnValue,
        Status: merchant.status,
        'Validation Status': merchant.errors.length > 0 ? 'Failed' : 'Valid',
        'Error Count': merchant.errors.length
      };
      // Add error details if any
      if (merchant.errors.length > 0) {
        exportRow['Error Details'] = merchant.errors.map(e => `${e.field}: ${e.message}`).join('; ');
      }
      return exportRow;
    });
    // Create worksheet from data
    const ws = XLSX.utils.json_to_sheet(exportData);
    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Merchants');
    // Generate filename
    const fileName = `Merchant_Batch_${new Date().toISOString().split('T')[0]}.xlsx`;
    // Write the workbook and trigger download
    XLSX.writeFile(wb, fileName);
  };
  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Get status badge based on status
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <StatusBadge status="success" text="Approved" data-id="element-1757" />;
      case 'rejected':
        return <StatusBadge status="danger" text="Rejected" data-id="element-1758" />;
      case 'pending':
        return <StatusBadge status="warning" text="Pending" data-id="element-1759" />;
      case 'error':
        return <StatusBadge status="danger" text="Error" data-id="element-1760" />;
      default:
        return <StatusBadge status="default" text={status} data-id="element-1761" />;
    }
  };
  // Show error details for a merchant
  const showErrorDetails = (merchant: Merchant) => {
    setSelectedMerchant(merchant);
    setShowErrorDetailModal(true);
  };
  // Generate template file for download
  const generateTemplateFile = () => {
    // Create a workbook with template structure
    const wb = XLSX.utils.book_new();
    // Define the column headers based on required fields
    const templateData = [{
      LegalName: '',
      DBA: '',
      'CNIC/NTN': '',
      Address: '',
      Mobile: '',
      Email: '',
      City: '',
      Country: '',
      'Account/IBAN': '',
      'Bank Name': '',
      'Bank Code': '',
      'Settlement Mode': '',
      MCC: '',
      'Business Type': '',
      MonthlyTxnCount: '',
      MonthlyTxnValue: ''
    }];
    // Create worksheet from template data
    const ws = XLSX.utils.json_to_sheet(templateData);
    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    // Generate filename
    const fileName = 'Merchant_Upload_Template.xlsx';
    // Write the workbook and trigger download
    XLSX.writeFile(wb, fileName);
  };
  // Columns for the merchant preview table
  const merchantColumns = [{
    header: 'Legal Name',
    accessor: 'legalName',
    sortable: true
  }, {
    header: 'DBA',
    accessor: 'dba',
    sortable: true
  }, {
    header: 'CNIC/NTN',
    accessor: 'cnicNtn',
    sortable: true
  }, {
    header: 'Contact',
    accessor: (merchant: Merchant) => <div data-id="element-1762">
          <div data-id="element-1763">{merchant.mobile}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1764">
            {merchant.email}
          </div>
        </div>,
    sortable: false
  }, {
    header: 'Location',
    accessor: (merchant: Merchant) => <div data-id="element-1765">
          <div data-id="element-1766">{merchant.city}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1767">
            {merchant.country}
          </div>
        </div>,
    sortable: false
  }, {
    header: 'Business Type',
    accessor: 'businessType',
    sortable: true
  }, {
    header: 'MCC',
    accessor: 'mcc',
    sortable: true
  }, {
    header: 'Status',
    accessor: (merchant: Merchant) => <div data-id="element-1768">
          {getStatusBadge(merchant.status)}
          {merchant.errors.length > 0 && <div className="mt-1 text-xs text-red-500 cursor-pointer hover:underline" onClick={e => {
        e.stopPropagation();
        showErrorDetails(merchant);
      }} data-id="element-1769">
              {merchant.errors.length}{' '}
              {merchant.errors.length === 1 ? 'error' : 'errors'}
            </div>}
        </div>,
    sortable: false
  }];
  // Columns for the batch request history table
  const batchColumns = [{
    header: 'Batch ID',
    accessor: 'id',
    sortable: true
  }, {
    header: 'Description',
    accessor: 'description',
    sortable: true
  }, {
    header: 'Requested By',
    accessor: 'requestedBy',
    sortable: true
  }, {
    header: 'Requested At',
    accessor: (batch: BatchRequest) => formatDate(batch.requestedAt),
    sortable: true
  }, {
    header: 'Merchants',
    accessor: (batch: BatchRequest) => <div data-id="element-1770">
          <div data-id="element-1771">{batch.merchantCount} total</div>
          <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1772">
            {batch.validCount} valid, {batch.errorCount} errors
          </div>
        </div>,
    sortable: false
  }, {
    header: 'Status',
    accessor: (batch: BatchRequest) => getStatusBadge(batch.status),
    sortable: false
  }];
  // Columns for the checker inbox table
  const checkerColumns = [...batchColumns, {
    header: 'Action Date',
    accessor: (batch: BatchRequest) => batch.status === 'approved' ? formatDate(batch.approvedAt || '') : batch.status === 'rejected' ? formatDate(batch.rejectedAt || '') : 'Pending',
    sortable: true
  }];
  // Function to view batch details
  const viewBatchDetails = (batch: BatchRequest) => {
    setSelectedBatchRequest(batch);
    setShowBatchDetailModal(true);
  };
  // Render different views based on the current view state
  const renderView = () => {
    switch (view) {
      case 'upload':
        return <>
            <div className="mb-6" data-id="element-1773">
              <Card data-id="element-1774">
                <div className="mb-4" data-id="element-1775">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-1776">
                    Upload Options
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1777">
                    <div data-id="element-1778">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1779">
                        Parent Merchant (Optional)
                      </label>
                      <select className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" value={selectedParentMerchant} onChange={e => setSelectedParentMerchant(e.target.value)} data-id="element-1780">
                        <option value="" data-id="element-1781">Create as Parent Merchants</option>
                        {parentMerchants.map(merchant => <option key={merchant.id} value={merchant.id} data-id="element-1782">
                            {merchant.name} ({merchant.id})
                          </option>)}
                      </select>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-1783">
                        If selected, all uploaded merchants will be created as
                        sub-merchants under this parent merchant. If not
                        selected, all merchants will be created as parent
                        merchants.
                      </p>
                    </div>
                    <div data-id="element-1784">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1785">
                        Batch Description (Optional)
                      </label>
                      <input type="text" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter batch description" value={batchDescription} onChange={e => setBatchDescription(e.target.value)} data-id="element-1786" />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-1787">
                        Add a description to help identify this batch of
                        merchants.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4" data-id="element-1788">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-1789">
                    Upload Merchant Data
                  </h2>
                  <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6" data-id="element-1790">
                    <div className="text-center" data-id="element-1791">
                      <UploadIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-1792" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white" data-id="element-1793">
                        Upload XLSX File
                      </h3>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-1794">
                        File should include all required merchant information in
                        the correct format.
                      </p>
                      <div className="mt-4" data-id="element-1795">
                        <input ref={fileInputRef} type="file" className="hidden" accept=".xlsx,.xls" onChange={handleFileChange} data-id="element-1796" />
                        <button type="button" onClick={handleUploadClick} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none" disabled={isProcessing} data-id="element-1797">
                          {isProcessing ? <>
                              <RefreshCwIcon className="animate-spin h-4 w-4 mr-2" data-id="element-1798" />
                              Processing...
                            </> : <>
                              <UploadIcon className="h-4 w-4 mr-2" data-id="element-1799" />
                              Select File
                            </>}
                        </button>
                      </div>
                      {fileReadError && <div className="mt-3 text-sm text-red-600 dark:text-red-400" data-id="element-1800">
                          <AlertCircleIcon className="inline-block h-4 w-4 mr-1" data-id="element-1801" />
                          {fileReadError}
                        </div>}
                    </div>
                  </div>
                  <div className="mt-4 flex items-start" data-id="element-1802">
                    <InfoIcon className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" data-id="element-1803" />
                    <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1804">
                      <p className="font-medium" data-id="element-1805">Required File Format</p>
                      <p className="mt-1" data-id="element-1806">
                        Your XLSX file must include columns for: LegalName, DBA,
                        CNIC/NTN, Address, Mobile, Email, City, Country,
                        Account/IBAN, Bank Name, Bank Code, Settlement Mode,
                        MCC, Business Type, MonthlyTxnCount, and
                        MonthlyTxnValue.
                      </p>
                      <button onClick={generateTemplateFile} className="mt-2 inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline" data-id="element-1807">
                        <DownloadIcon className="h-4 w-4 mr-1" data-id="element-1808" />
                        Download Template
                      </button>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
            {uploadedMerchants.length > 0 && <Card data-id="element-1809">
                <div className="flex justify-between items-center mb-4" data-id="element-1810">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-1811">
                    Preview Uploaded Merchants
                  </h2>
                  <div className="space-x-2" data-id="element-1812">
                    <button onClick={handleExportAll} className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-1813">
                      <DownloadIcon className="h-4 w-4 mr-1" data-id="element-1814" />
                      Export All
                    </button>
                    <button onClick={() => setUploadedMerchants([])} className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-1815">
                      <XIcon className="h-4 w-4 mr-1" data-id="element-1816" />
                      Clear
                    </button>
                    <button onClick={() => setShowSubmitModal(true)} className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" disabled={uploadedMerchants.filter(m => m.errors.length === 0).length === 0} data-id="element-1817">
                      <CheckIcon className="h-4 w-4 mr-1" data-id="element-1818" />
                      Submit for Approval
                    </button>
                  </div>
                </div>
                {/* Validation Summary */}
                <div className="mb-4 bg-gray-50 dark:bg-gray-800 rounded-lg p-4" data-id="element-1819">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-1820">
                    <div data-id="element-1821">
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-1822">
                        Validation Summary
                      </h3>
                      <div className="mt-1 flex items-center space-x-4" data-id="element-1823">
                        <div className="flex items-center" data-id="element-1824">
                          <div className="h-3 w-3 rounded-full bg-green-500 mr-1" data-id="element-1825"></div>
                          <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1826">
                            {uploadedMerchants.filter(m => m.errors.length === 0).length}{' '}
                            Valid
                          </span>
                        </div>
                        <div className="flex items-center" data-id="element-1827">
                          <div className="h-3 w-3 rounded-full bg-red-500 mr-1" data-id="element-1828"></div>
                          <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1829">
                            {uploadedMerchants.filter(m => m.errors.length > 0).length}{' '}
                            Failed
                          </span>
                        </div>
                        <div className="flex items-center" data-id="element-1830">
                          <div className="h-3 w-3 rounded-full bg-gray-300 dark:bg-gray-600 mr-1" data-id="element-1831"></div>
                          <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1832">
                            {uploadedMerchants.length} Total
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 sm:mt-0" data-id="element-1833">
                      {uploadedMerchants.length > 10 && <div className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-xs font-medium" data-id="element-1834">
                          Large batch: {uploadedMerchants.length} merchants
                        </div>}
                    </div>
                  </div>
                </div>
                <div className="mb-3 flex items-center justify-between" data-id="element-1835">
                  <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1836">
                    Showing {uploadedMerchants.length} merchants from{' '}
                    {uploadedFile?.name}
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-1837">
                    <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1838">
                      With errors:
                    </span>
                    <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 rounded-full text-xs font-medium" data-id="element-1839">
                      {uploadedMerchants.filter(m => m.errors.length > 0).length}
                    </span>
                  </div>
                </div>
                <Table columns={merchantColumns} data={uploadedMerchants} keyField="id" actions={merchant => <div className="flex justify-end space-x-2" data-id="element-1841">
                      <button onClick={() => {
                // View merchant details
              }} className="p-1 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700" title="View Details" data-id="element-1842">
                        <FileTextIcon className="h-5 w-5" data-id="element-1843" />
                      </button>
                      {merchant.errors.length > 0 && <button onClick={() => showErrorDetails(merchant)} className="p-1 rounded-full text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30" title="View Errors" data-id="element-1844">
                          <AlertCircleIcon className="h-5 w-5" data-id="element-1845" />
                        </button>}
                    </div>} data-id="element-1840" />
                <div className="mt-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3" data-id="element-1846">
                  <div className="flex" data-id="element-1847">
                    <InfoIcon className="h-5 w-5 text-yellow-400 mr-2" data-id="element-1848" />
                    <div className="text-sm text-yellow-700 dark:text-yellow-300" data-id="element-1849">
                      <p className="font-medium" data-id="element-1850">Disclaimer</p>
                      <p className="mt-1" data-id="element-1851">
                        Only valid merchants will be forwarded for approval.
                        Records with validation errors (
                        {uploadedMerchants.filter(m => m.errors.length > 0).length}
                        ) will be dropped by the system during processing. You
                        can export the full list including errors for your
                        records.
                      </p>
                    </div>
                  </div>
                </div>
              </Card>}
          </>;
      case 'history':
        return <Card data-id="element-1852">
            <div className="flex justify-between items-center mb-4" data-id="element-1853">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-1854">
                Batch Upload History
              </h2>
              <div className="space-x-2" data-id="element-1855">
                <button onClick={() => setView('upload')} className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-1856">
                  <PlusIcon className="h-4 w-4 mr-1" data-id="element-1857" />
                  New Upload
                </button>
              </div>
            </div>
            <div className="mb-4 flex items-center space-x-2" data-id="element-1858">
              <div className="relative flex-grow max-w-xs" data-id="element-1859">
                <input type="text" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 pl-8 pr-3 text-sm" placeholder="Search batches..." data-id="element-1860" />
                <div className="absolute inset-y-0 left-0 flex items-center pl-2" data-id="element-1861">
                  <FilterIcon className="h-4 w-4 text-gray-400" data-id="element-1862" />
                </div>
              </div>
              <select className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 pl-3 pr-8 text-sm" data-id="element-1863">
                <option value="" data-id="element-1864">All Statuses</option>
                <option value="pending" data-id="element-1865">Pending</option>
                <option value="approved" data-id="element-1866">Approved</option>
                <option value="rejected" data-id="element-1867">Rejected</option>
              </select>
            </div>
            <Table columns={batchColumns} data={batchRequests} keyField="id" actions={batch => <div className="flex justify-end space-x-2" data-id="element-1869">
                  <button onClick={() => viewBatchDetails(batch)} className="p-1 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700" title="View Details" data-id="element-1870">
                    <FileTextIcon className="h-5 w-5" data-id="element-1871" />
                  </button>
                  <button className="p-1 rounded-full text-green-500 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30" title="Download Results" data-id="element-1872">
                    <DownloadIcon className="h-5 w-5" data-id="element-1873" />
                  </button>
                </div>} data-id="element-1868" />
          </Card>;
      case 'checker':
        return <Card data-id="element-1874">
            <div className="flex justify-between items-center mb-4" data-id="element-1875">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-1876">
                Merchant Batch Approval Queue
              </h2>
              <div className="space-x-2" data-id="element-1877">
                <button className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-1878">
                  <RefreshCwIcon className="h-4 w-4 mr-1" data-id="element-1879" />
                  Refresh
                </button>
              </div>
            </div>
            <div className="mb-4 flex items-center justify-between" data-id="element-1880">
              <div className="flex items-center space-x-2" data-id="element-1881">
                <div className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 rounded-full text-xs font-medium flex items-center" data-id="element-1882">
                  <ClockIcon className="h-3 w-3 mr-1" data-id="element-1883" />
                  <span data-id="element-1884">
                    {batchRequests.filter(b => b.status === 'pending').length}{' '}
                    Pending
                  </span>
                </div>
                <div className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full text-xs font-medium flex items-center" data-id="element-1885">
                  <CheckIcon className="h-3 w-3 mr-1" data-id="element-1886" />
                  <span data-id="element-1887">
                    {batchRequests.filter(b => b.status === 'approved').length}{' '}
                    Approved
                  </span>
                </div>
                <div className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 rounded-full text-xs font-medium flex items-center" data-id="element-1888">
                  <XIcon className="h-3 w-3 mr-1" data-id="element-1889" />
                  <span data-id="element-1890">
                    {batchRequests.filter(b => b.status === 'rejected').length}{' '}
                    Rejected
                  </span>
                </div>
              </div>
              <div className="relative" data-id="element-1891">
                <select className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-1.5 pl-3 pr-8 text-sm" data-id="element-1892">
                  <option value="" data-id="element-1893">All Batches</option>
                  <option value="pending" data-id="element-1894">Pending Only</option>
                </select>
              </div>
            </div>
            <Table columns={checkerColumns} data={batchRequests} keyField="id" actions={batch => <div className="flex justify-end space-x-2" data-id="element-1896">
                  <button onClick={() => viewBatchDetails(batch)} className="p-1 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700" title="View Details" data-id="element-1897">
                    <FileTextIcon className="h-5 w-5" data-id="element-1898" />
                  </button>
                  {batch.status === 'pending' && <>
                      <button onClick={() => {
                setSelectedBatchRequest(batch);
                setShowApproveModal(true);
              }} className="p-1 rounded-full text-green-500 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30" title="Approve" data-id="element-1899">
                        <CheckIcon className="h-5 w-5" data-id="element-1900" />
                      </button>
                      <button onClick={() => {
                setSelectedBatchRequest(batch);
                setShowRejectModal(true);
              }} className="p-1 rounded-full text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30" title="Reject" data-id="element-1901">
                        <XIcon className="h-5 w-5" data-id="element-1902" />
                      </button>
                    </>}
                </div>} data-id="element-1895" />
          </Card>;
      default:
        return null;
    }
  };
  return <div className="space-y-6" data-id="element-1903">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-1904">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1905">
          Bulk Merchant Upload
        </h1>
        <div className="mt-3 sm:mt-0 flex space-x-2" data-id="element-1906">
          <button onClick={() => setView('upload')} className={`px-4 py-2 rounded-md text-sm font-medium ${view === 'upload' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`} data-id="element-1907">
            Upload
          </button>
          <button onClick={() => setView('history')} className={`px-4 py-2 rounded-md text-sm font-medium ${view === 'history' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`} data-id="element-1908">
            History
          </button>
          {user.role === 'checker' && <button onClick={() => setView('checker')} className={`px-4 py-2 rounded-md text-sm font-medium ${view === 'checker' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`} data-id="element-1909">
              Checker Inbox
            </button>}
        </div>
      </div>
      {renderView()}
      {/* File Preview Modal */}
      <Modal isOpen={showPreviewModal} onClose={() => setShowPreviewModal(false)} title="File Preview" size="lg" data-id="element-1910">
        <div className="space-y-4" data-id="element-1911">
          <div className="flex items-center justify-between" data-id="element-1912">
            <div data-id="element-1913">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-1914">
                {uploadedFile?.name || 'merchants.xlsx'}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1915">
                {uploadedMerchants.length} merchants found in file
              </p>
            </div>
            <StatusBadge status={uploadedMerchants.some(m => m.errors.length > 0) ? 'warning' : 'success'} text={uploadedMerchants.some(m => m.errors.length > 0) ? 'Validation Issues' : 'Valid'} data-id="element-1916" />
          </div>
          {/* Validation Summary */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4" data-id="element-1917">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1918">
              Validation Summary
            </h4>
            <div className="grid grid-cols-3 gap-2" data-id="element-1919">
              <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-md text-center" data-id="element-1920">
                <div className="text-lg font-semibold text-green-700 dark:text-green-300" data-id="element-1921">
                  {uploadedMerchants.filter(m => m.errors.length === 0).length}
                </div>
                <div className="text-xs text-green-600 dark:text-green-400" data-id="element-1922">
                  Valid Records
                </div>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-md text-center" data-id="element-1923">
                <div className="text-lg font-semibold text-red-700 dark:text-red-300" data-id="element-1924">
                  {uploadedMerchants.filter(m => m.errors.length > 0).length}
                </div>
                <div className="text-xs text-red-600 dark:text-red-400" data-id="element-1925">
                  Failed Records
                </div>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-md text-center" data-id="element-1926">
                <div className="text-lg font-semibold text-blue-700 dark:text-blue-300" data-id="element-1927">
                  {uploadedMerchants.length}
                </div>
                <div className="text-xs text-blue-600 dark:text-blue-400" data-id="element-1928">
                  Total Records
                </div>
              </div>
            </div>
            {uploadedMerchants.length > 10 && <div className="mt-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-2 text-sm text-yellow-700 dark:text-yellow-300 flex items-center" data-id="element-1929">
                <AlertCircleIcon className="h-4 w-4 mr-2 text-yellow-500" data-id="element-1930" />
                Large batch detected: {uploadedMerchants.length} merchants (more
                than 10)
              </div>}
          </div>
          {uploadedMerchants.some(m => m.errors.length > 0) && <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3" data-id="element-1931">
              <div className="flex" data-id="element-1932">
                <AlertCircleIcon className="h-5 w-5 text-yellow-400 mr-2" data-id="element-1933" />
                <div className="text-sm text-yellow-700 dark:text-yellow-300" data-id="element-1934">
                  <p className="font-medium" data-id="element-1935">
                    Some merchants have validation issues
                  </p>
                  <p className="mt-1" data-id="element-1936">
                    Only valid merchants will be processed. Records with errors
                    will be dropped during processing. You can export the full
                    list including errors for your records.
                  </p>
                </div>
              </div>
            </div>}
          <div className="max-h-96 overflow-y-auto" data-id="element-1937">
            <Table columns={merchantColumns} data={uploadedMerchants} keyField="id" pagination={false} data-id="element-1938" />
          </div>
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-1939">
            <button onClick={handleExportAll} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-1940">
              <DownloadIcon className="h-4 w-4 mr-1 inline-block" data-id="element-1941" />
              Export All
            </button>
            <button onClick={() => setShowPreviewModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-1942">
              Close
            </button>
            <button onClick={() => {
            setShowPreviewModal(false);
            setShowSubmitModal(true);
          }} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" disabled={uploadedMerchants.filter(m => m.errors.length === 0).length === 0} data-id="element-1943">
              Proceed to Submit
            </button>
          </div>
        </div>
      </Modal>
      {/* Error Detail Modal */}
      <Modal isOpen={showErrorDetailModal} onClose={() => setShowErrorDetailModal(false)} title="Validation Error Details" data-id="element-1944">
        {selectedMerchant && <div className="space-y-4" data-id="element-1945">
            <div className="flex items-center" data-id="element-1946">
              <div className="p-2 rounded-full bg-red-100 dark:bg-red-900/30 mr-3" data-id="element-1947">
                <AlertCircleIcon className="h-5 w-5 text-red-500" data-id="element-1948" />
              </div>
              <div data-id="element-1949">
                <h3 className="text-lg font-medium" data-id="element-1950">
                  {selectedMerchant.legalName || selectedMerchant.dba}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1951">
                  {selectedMerchant.errors.length} validation{' '}
                  {selectedMerchant.errors.length === 1 ? 'error' : 'errors'}{' '}
                  found
                </p>
              </div>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4" data-id="element-1952">
              <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1953">
                Error Details
              </h4>
              <div className="space-y-3" data-id="element-1954">
                {selectedMerchant.errors.map((error, index) => <div key={index} className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3" data-id="element-1955">
                    <div className="flex items-start" data-id="element-1956">
                      <XIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5" data-id="element-1957" />
                      <div data-id="element-1958">
                        <div className="font-medium text-red-700 dark:text-red-300" data-id="element-1959">
                          {error.field}
                        </div>
                        <div className="text-sm text-red-600 dark:text-red-400 mt-1" data-id="element-1960">
                          {error.message}
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-md p-3 mt-2" data-id="element-1961">
              <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1962">
                Merchant Information
              </h4>
              <div className="grid grid-cols-2 gap-2 text-sm" data-id="element-1963">
                <div data-id="element-1964">
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-1965">
                    Legal Name:
                  </span>{' '}
                  {selectedMerchant.legalName || '—'}
                </div>
                <div data-id="element-1966">
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-1967">DBA:</span>{' '}
                  {selectedMerchant.dba || '—'}
                </div>
                <div data-id="element-1968">
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-1969">
                    CNIC/NTN:
                  </span>{' '}
                  {selectedMerchant.cnicNtn || '—'}
                </div>
                <div data-id="element-1970">
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-1971">
                    Email:
                  </span>{' '}
                  {selectedMerchant.email || '—'}
                </div>
                <div data-id="element-1972">
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-1973">
                    Mobile:
                  </span>{' '}
                  {selectedMerchant.mobile || '—'}
                </div>
                <div data-id="element-1974">
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-1975">
                    IBAN:
                  </span>{' '}
                  {selectedMerchant.accountIban || '—'}
                </div>
              </div>
            </div>
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-1976">
              <div className="flex justify-end" data-id="element-1977">
                <button onClick={() => setShowErrorDetailModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-1978">
                  Close
                </button>
              </div>
            </div>
          </div>}
      </Modal>
      {/* Submit Confirmation Modal */}
      <Modal isOpen={showSubmitModal} onClose={() => setShowSubmitModal(false)} title="Submit for Approval" data-id="element-1979">
        <div className="space-y-4" data-id="element-1980">
          <p className="text-gray-700 dark:text-gray-300" data-id="element-1981">
            You are about to submit a batch of merchants for approval.
            {selectedParentMerchant ? ` They will be created as sub-merchants under ${parentMerchants.find(m => m.id === selectedParentMerchant)?.name}.` : ' They will be created as parent merchants.'}
          </p>
          {/* Submission Summary */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4" data-id="element-1982">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1983">
              Submission Summary
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-1984">
              <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-md text-center" data-id="element-1985">
                <div className="text-lg font-semibold text-green-700 dark:text-green-300" data-id="element-1986">
                  {uploadedMerchants.filter(m => m.errors.length === 0).length}
                </div>
                <div className="text-xs text-green-600 dark:text-green-400" data-id="element-1987">
                  Valid Records
                </div>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-md text-center" data-id="element-1988">
                <div className="text-lg font-semibold text-red-700 dark:text-red-300" data-id="element-1989">
                  {uploadedMerchants.filter(m => m.errors.length > 0).length}
                </div>
                <div className="text-xs text-red-600 dark:text-red-400" data-id="element-1990">
                  Failed Records
                </div>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-md text-center" data-id="element-1991">
                <div className="text-lg font-semibold text-blue-700 dark:text-blue-300" data-id="element-1992">
                  {uploadedMerchants.length}
                </div>
                <div className="text-xs text-blue-600 dark:text-blue-400" data-id="element-1993">
                  Total Records
                </div>
              </div>
            </div>
          </div>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3" data-id="element-1994">
            <div className="flex" data-id="element-1995">
              <AlertCircleIcon className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0" data-id="element-1996" />
              <div className="text-sm text-yellow-700 dark:text-yellow-300" data-id="element-1997">
                <p className="font-medium" data-id="element-1998">Important Notice</p>
                <p className="mt-1" data-id="element-1999">
                  Only valid merchants (
                  {uploadedMerchants.filter(m => m.errors.length === 0).length}
                  ) will be submitted for approval. Records with validation
                  errors (
                  {uploadedMerchants.filter(m => m.errors.length > 0).length})
                  will be dropped during processing.
                </p>
              </div>
            </div>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3" data-id="element-2000">
            <div className="flex" data-id="element-2001">
              <InfoIcon className="h-5 w-5 text-blue-400 mr-2" data-id="element-2002" />
              <div className="text-sm text-blue-700 dark:text-blue-300" data-id="element-2003">
                <p className="font-medium" data-id="element-2004">Approval Process</p>
                <p className="mt-1" data-id="element-2005">
                  Your submission will be reviewed by a checker. Once approved,
                  Merchant IDs (MIDs) will be generated and you'll be able to
                  download the results.
                </p>
              </div>
            </div>
          </div>
          <div className="pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-2006">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2007">
              Batch Description
            </label>
            <input type="text" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter a description for this batch" value={batchDescription} onChange={e => setBatchDescription(e.target.value)} data-id="element-2008" />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-2009">
              Adding a clear description helps reviewers understand the purpose
              of this merchant batch.
            </p>
          </div>
          <div className="flex justify-end space-x-3 pt-4 mt-4" data-id="element-2010">
            <button onClick={() => setShowSubmitModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" disabled={isSubmitting} data-id="element-2011">
              Cancel
            </button>
            <button onClick={handleSubmitBatch} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 flex items-center" disabled={isSubmitting} data-id="element-2012">
              {isSubmitting ? <>
                  <RefreshCwIcon className="animate-spin h-4 w-4 mr-2" data-id="element-2013" />
                  Submitting...
                </> : <>
                  <CheckIcon className="h-4 w-4 mr-2" data-id="element-2014" />
                  Submit for Approval
                </>}
            </button>
          </div>
        </div>
      </Modal>
      {/* Success Modal */}
      <Modal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)} title="Submission Successful" data-id="element-2015">
        <div className="text-center py-4" data-id="element-2016">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30" data-id="element-2017">
            <CheckIcon className="h-6 w-6 text-green-600 dark:text-green-400" data-id="element-2018" />
          </div>
          <h3 className="mt-3 text-lg font-medium text-gray-900 dark:text-white" data-id="element-2019">
            Merchants Submitted Successfully
          </h3>
          <div className="mt-4 bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-left" data-id="element-2020">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2021">
              Submission Summary
            </h4>
            <div className="flex items-center justify-between mb-2" data-id="element-2022">
              <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-2023">
                Total Records:
              </span>
              <span className="text-sm font-medium" data-id="element-2024">
                {submissionStats.totalCount}
              </span>
            </div>
            <div className="flex items-center justify-between mb-2" data-id="element-2025">
              <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-2026">
                Successfully Submitted:
              </span>
              <span className="text-sm font-medium text-green-600 dark:text-green-400" data-id="element-2027">
                {submissionStats.validCount}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-2028">
              <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-2029">
                Failed Records:
              </span>
              <span className="text-sm font-medium text-red-600 dark:text-red-400" data-id="element-2030">
                {submissionStats.errorCount}
              </span>
            </div>
          </div>
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400" data-id="element-2031">
            Your batch has been submitted for approval. You'll be notified once
            it's approved.
          </p>
          <div className="mt-4 flex justify-center space-x-3" data-id="element-2032">
            <button onClick={() => {
            setShowSuccessModal(false);
            setView('history');
          }} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-2033">
              View Submission History
            </button>
            <button onClick={handleExportAll} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-2034">
              <DownloadIcon className="h-4 w-4 mr-1 inline-block" data-id="element-2035" />
              Export Results
            </button>
          </div>
        </div>
      </Modal>
      {/* Batch Detail Modal */}
      <Modal isOpen={showBatchDetailModal} onClose={() => setShowBatchDetailModal(false)} title="Batch Request Details" size="lg" data-id="element-2036">
        {selectedBatchRequest && <div className="space-y-4" data-id="element-2037">
            <div className="flex items-center justify-between" data-id="element-2038">
              <div data-id="element-2039">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-2040">
                  {selectedBatchRequest.description}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2041">
                  Batch ID: {selectedBatchRequest.id}
                </p>
              </div>
              {getStatusBadge(selectedBatchRequest.status)}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 dark:bg-gray-800 rounded-lg p-4" data-id="element-2042">
              <div data-id="element-2043">
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2044">
                  Requested By
                </p>
                <p className="font-medium" data-id="element-2045">
                  {selectedBatchRequest.requestedBy}
                </p>
              </div>
              <div data-id="element-2046">
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2047">
                  Requested At
                </p>
                <p className="font-medium" data-id="element-2048">
                  {formatDate(selectedBatchRequest.requestedAt)}
                </p>
              </div>
              <div data-id="element-2049">
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2050">
                  Merchant Count
                </p>
                <p className="font-medium" data-id="element-2051">
                  {selectedBatchRequest.merchantCount} total
                  <span className="text-sm text-gray-500 dark:text-gray-400 ml-2" data-id="element-2052">
                    ({selectedBatchRequest.validCount} valid,{' '}
                    {selectedBatchRequest.errorCount} errors)
                  </span>
                </p>
              </div>
              <div data-id="element-2053">
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2054">
                  Status
                </p>
                <p className="font-medium" data-id="element-2055">
                  {selectedBatchRequest.status.charAt(0).toUpperCase() + selectedBatchRequest.status.slice(1)}
                </p>
              </div>
              {selectedBatchRequest.status === 'approved' && <>
                  <div data-id="element-2056">
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2057">
                      Approved By
                    </p>
                    <p className="font-medium" data-id="element-2058">
                      {selectedBatchRequest.approvedBy}
                    </p>
                  </div>
                  <div data-id="element-2059">
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2060">
                      Approved At
                    </p>
                    <p className="font-medium" data-id="element-2061">
                      {selectedBatchRequest.approvedAt ? formatDate(selectedBatchRequest.approvedAt) : '-'}
                    </p>
                  </div>
                </>}
              {selectedBatchRequest.status === 'rejected' && <>
                  <div data-id="element-2062">
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2063">
                      Rejected By
                    </p>
                    <p className="font-medium" data-id="element-2064">
                      {selectedBatchRequest.rejectedBy}
                    </p>
                  </div>
                  <div data-id="element-2065">
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2066">
                      Rejected At
                    </p>
                    <p className="font-medium" data-id="element-2067">
                      {selectedBatchRequest.rejectedAt ? formatDate(selectedBatchRequest.rejectedAt) : '-'}
                    </p>
                  </div>
                  <div className="md:col-span-2" data-id="element-2068">
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2069">
                      Rejection Reason
                    </p>
                    <p className="font-medium" data-id="element-2070">
                      {selectedBatchRequest.rejectionReason || '-'}
                    </p>
                  </div>
                </>}
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4" data-id="element-2071">
              <div className="flex justify-between items-center mb-3" data-id="element-2072">
                <h4 className="font-medium text-gray-900 dark:text-white" data-id="element-2073">
                  Merchants in Batch
                </h4>
                <button className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-2074">
                  <DownloadIcon className="h-4 w-4 mr-1" data-id="element-2075" />
                  Export List
                </button>
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-3" data-id="element-2076">
                Merchant data is not available for viewing in this demo.
              </div>
            </div>
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-2077">
              {selectedBatchRequest.status === 'approved' && <button className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 flex items-center" data-id="element-2078">
                  <DownloadIcon className="h-4 w-4 mr-2" data-id="element-2079" />
                  Download Results
                </button>}
              {selectedBatchRequest.status === 'pending' && view === 'checker' && <>
                    <button onClick={() => {
              setShowBatchDetailModal(false);
              setShowRejectModal(true);
            }} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-red-600 dark:text-red-400 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/10" data-id="element-2080">
                      Reject
                    </button>
                    <button onClick={() => {
              setShowBatchDetailModal(false);
              setShowApproveModal(true);
            }} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-2081">
                      Approve
                    </button>
                  </>}
              <button onClick={() => setShowBatchDetailModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-2082">
                Close
              </button>
            </div>
          </div>}
      </Modal>
      {/* Approve Modal */}
      <Modal isOpen={showApproveModal} onClose={() => setShowApproveModal(false)} title="Approve Merchant Batch" footer={<>
            <button onClick={() => setShowApproveModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-2084">
              Cancel
            </button>
            <button onClick={handleApproveBatch} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700" data-id="element-2085">
              Approve Batch
            </button>
          </>} data-id="element-2083">
        {selectedBatchRequest && <div className="space-y-4" data-id="element-2086">
            <p className="text-gray-700 dark:text-gray-300" data-id="element-2087">
              You are about to approve batch{' '}
              <strong data-id="element-2088">{selectedBatchRequest.id}</strong> containing{' '}
              <strong data-id="element-2089">{selectedBatchRequest.validCount}</strong> valid
              merchants.
            </p>
            <p className="text-gray-700 dark:text-gray-300" data-id="element-2090">
              This will generate Merchant IDs (MIDs) for all valid merchants in
              the batch and make them available in the system.
            </p>
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-3" data-id="element-2091">
              <div className="flex" data-id="element-2092">
                <CheckIcon className="h-5 w-5 text-green-400 mr-2" data-id="element-2093" />
                <div className="text-sm text-green-700 dark:text-green-300" data-id="element-2094">
                  <p data-id="element-2095">
                    Merchant IDs will be automatically generated according to
                    the system's rules.
                  </p>
                </div>
              </div>
            </div>
            {selectedBatchRequest.errorCount > 0 && <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3" data-id="element-2096">
                <div className="flex" data-id="element-2097">
                  <InfoIcon className="h-5 w-5 text-yellow-400 mr-2" data-id="element-2098" />
                  <div className="text-sm text-yellow-700 dark:text-yellow-300" data-id="element-2099">
                    <p data-id="element-2100">
                      Note: {selectedBatchRequest.errorCount} merchants with
                      validation errors were dropped during processing and will
                      not be included in this approval.
                    </p>
                  </div>
                </div>
              </div>}
          </div>}
      </Modal>
      {/* Reject Modal */}
      <Modal isOpen={showRejectModal} onClose={() => setShowRejectModal(false)} title="Reject Merchant Batch" footer={<>
            <button onClick={() => setShowRejectModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-2102">
              Cancel
            </button>
            <button onClick={handleRejectBatch} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700" disabled={!rejectionReason.trim()} data-id="element-2103">
              Reject Batch
            </button>
          </>} data-id="element-2101">
        {selectedBatchRequest && <div className="space-y-4" data-id="element-2104">
            <p className="text-gray-700 dark:text-gray-300" data-id="element-2105">
              You are about to reject batch{' '}
              <strong data-id="element-2106">{selectedBatchRequest.id}</strong> containing{' '}
              <strong data-id="element-2107">{selectedBatchRequest.validCount}</strong> valid
              merchants.
            </p>
            <div data-id="element-2108">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2109">
                Rejection Reason <span className="text-red-500" data-id="element-2110">*</span>
              </label>
              <textarea rows={3} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Please provide a reason for rejection" value={rejectionReason} onChange={e => setRejectionReason(e.target.value)} required data-id="element-2111" />
            </div>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3" data-id="element-2112">
              <div className="flex" data-id="element-2113">
                <AlertCircleIcon className="h-5 w-5 text-yellow-400 mr-2" data-id="element-2114" />
                <div className="text-sm text-yellow-700 dark:text-yellow-300" data-id="element-2115">
                  <p data-id="element-2116">
                    The maker will need to create a new batch to address the
                    issues you've identified.
                  </p>
                </div>
              </div>
            </div>
          </div>}
      </Modal>
    </div>;
};
export default BulkMerchant;